/**
 * The leaderboard.
 *
 * Broadcast conventions on purpose: shared positions read T4, movement arrows
 * compare against the last computed board, "thru" shows F when a player is home,
 * and every number is tabular so nothing jitters on refresh. Columns come from
 * the format engine, so a Stableford board shows points and a match-play board
 * shows holes up without this component knowing either format exists.
 */
import { Fragment, useMemo, useRef, useState } from 'react';
import type { LeaderboardColumn, LeaderboardPayload, LeaderboardRow } from '@fairwaylive/shared';
import { AdZone } from './AdZone.tsx';

export type SortDirection = 'asc' | 'desc';

interface LeaderboardProps {
  payload: LeaderboardPayload;
  /** Inject the in-feed ad unit after every N rows. 0 disables it. */
  adEveryNRows?: number;
  onSelectRow?: (row: LeaderboardRow) => void;
  compact?: boolean;
  /** Big-screen mode: larger type, no interaction. */
  display?: boolean;
  maxRows?: number;
}

const flagFor = (code: string | null | undefined): string => {
  if (!code || code.length !== 2) return '🏳️';
  // Regional indicator pair — renders the flag emoji for the ISO code.
  return String.fromCodePoint(...[...code.toUpperCase()].map((c) => 0x1f1a5 + c.charCodeAt(0)));
};

function cellValue(row: LeaderboardRow, column: LeaderboardColumn): string {
  switch (column.key) {
    case 'position':
      return row.positionLabel;
    case 'player':
      return row.displayName;
    case 'primary':
      return row.primaryLabel;
    case 'today':
      return row.todayLabel;
    case 'thru':
      return row.thruLabel;
    case 'strokes':
      return row.strokes === null ? '—' : String(row.strokes);
    default: {
      const extra = row.extras?.[column.key];
      return extra === null || extra === undefined ? '—' : String(extra);
    }
  }
}

function sortValue(row: LeaderboardRow, key: string): number | string {
  switch (key) {
    case 'position':
      return row.position;
    case 'player':
      return row.displayName;
    case 'primary':
      return row.primary;
    case 'today':
      return row.today ?? Number.POSITIVE_INFINITY;
    case 'thru':
      return row.thru;
    case 'strokes':
      return row.strokes ?? Number.POSITIVE_INFINITY;
    default: {
      const extra = row.extras?.[key];
      if (typeof extra === 'number') return extra;
      return extra === null || extra === undefined ? '' : String(extra);
    }
  }
}

function Movement({ row, display }: { row: LeaderboardRow; display?: boolean }) {
  const size = display ? 'text-xl' : 'text-[13px]';
  if (row.movement === 'up') {
    return (
      <span className={`${size} text-[var(--color-fairway-300)]`} title={`Up ${row.movementDelta}`} aria-label={`Up ${row.movementDelta} places`}>
        ▲{row.movementDelta > 1 && !display ? <span className="tabular text-[10px]">{row.movementDelta}</span> : null}
      </span>
    );
  }
  if (row.movement === 'down') {
    return (
      <span className={`${size} text-[var(--color-rose-sun)]`} title={`Down ${row.movementDelta}`} aria-label={`Down ${row.movementDelta} places`}>
        ▼{row.movementDelta > 1 && !display ? <span className="tabular text-[10px]">{row.movementDelta}</span> : null}
      </span>
    );
  }
  if (row.movement === 'new') {
    return <span className="text-[10px] uppercase tracking-wider text-[var(--color-gold-500)]">new</span>;
  }
  return <span className="text-[var(--color-fairway-600)]">–</span>;
}

const statusBadge: Record<LeaderboardRow['status'], { label: string; tone: string } | null> = {
  not_started: null,
  in_progress: null,
  finished: null,
  cut: { label: 'CUT', tone: 'text-[var(--color-muted-dim)]' },
  wd: { label: 'WD', tone: 'text-[var(--color-rose-sun)]' },
  dq: { label: 'DQ', tone: 'text-[var(--color-rose-sun)]' },
  nr: { label: 'NR', tone: 'text-[var(--color-muted-dim)]' },
};

export function Leaderboard({
  payload,
  adEveryNRows = 0,
  onSelectRow,
  compact = false,
  display = false,
  maxRows,
}: LeaderboardProps) {
  const [sortKey, setSortKey] = useState<string>('position');
  const [direction, setDirection] = useState<SortDirection>('asc');
  const previousVersion = useRef(payload.version);
  const changed = previousVersion.current !== payload.version;
  previousVersion.current = payload.version;

  const columns = useMemo(
    () => payload.columns.filter((c) => (compact ? c.hideBelowPx === 0 || c.key === 'primary' : true)),
    [compact, payload.columns],
  );

  const rows = useMemo(() => {
    const sorted = [...payload.rows].sort((a, b) => {
      const av = sortValue(a, sortKey);
      const bv = sortValue(b, sortKey);
      if (typeof av === 'string' || typeof bv === 'string') {
        return direction === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
      }
      return direction === 'asc' ? av - bv : bv - av;
    });
    return maxRows ? sorted.slice(0, maxRows) : sorted;
  }, [direction, maxRows, payload.rows, sortKey]);

  const toggleSort = (column: LeaderboardColumn) => {
    if (display || !column.sortable) return;
    if (sortKey === column.key) {
      setDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(column.key);
      // Position and stroke columns read low-to-high; points read high-to-low.
      setDirection(column.key === 'primary' && payload.formatKey.includes('stableford') ? 'desc' : 'asc');
    }
  };

  const textSize = display ? 'text-[26px]' : 'text-[14px]';
  const rowPad = display ? 'py-3.5' : compact ? 'py-2' : 'py-2.5';

  return (
    <div className="overflow-hidden rounded-[var(--radius-card)] border border-[color-mix(in_oklab,var(--color-fairway-400)_20%,transparent)] bg-[var(--color-fairway-850)]">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-[color-mix(in_oklab,var(--color-fairway-400)_26%,transparent)] bg-[var(--color-fairway-800)]">
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                onClick={() => toggleSort(column)}
                aria-sort={sortKey === column.key ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                className={`board-title px-2 ${display ? 'py-4 text-base' : 'py-2.5 text-[11px]'} font-semibold text-[var(--color-muted)] ${
                  column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left'
                } ${column.sortable && !display ? 'cursor-pointer select-none hover:text-[var(--color-fairway-300)]' : ''} ${
                  column.hideBelowPx >= 1100 ? 'hidden xl:table-cell' : column.hideBelowPx >= 900 ? 'hidden lg:table-cell' : column.hideBelowPx >= 768 ? 'hidden md:table-cell' : column.hideBelowPx >= 640 ? 'hidden sm:table-cell' : ''
                }`}
              >
                <span className="hidden sm:inline">{column.label}</span>
                <span className="sm:hidden">{column.shortLabel}</span>
                {sortKey === column.key && !display ? (
                  <span className="ml-1 text-[9px] text-[var(--color-fairway-300)]">{direction === 'asc' ? '▲' : '▼'}</span>
                ) : null}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const badge = statusBadge[row.status];
            const leader = row.position === 1 && sortKey === 'position';
            return (
              <Fragment key={row.entryId}>
                <tr
                  onClick={() => onSelectRow?.(row)}
                  className={`border-b border-[color-mix(in_oklab,var(--color-fairway-400)_10%,transparent)] ${
                    onSelectRow && !display ? 'cursor-pointer' : ''
                  } ${changed && row.movement !== 'same' ? 'row-flash' : ''} ${
                    leader ? 'bg-[color-mix(in_oklab,var(--color-fairway-500)_22%,transparent)]' : i % 2 ? 'bg-[color-mix(in_oklab,var(--color-fairway-800)_45%,transparent)]' : ''
                  } ${!display && onSelectRow ? 'hover:bg-[color-mix(in_oklab,var(--color-fairway-400)_12%,transparent)]' : ''}`}
                >
                  {columns.map((column) => {
                    const hideClass =
                      column.hideBelowPx >= 1100
                        ? 'hidden xl:table-cell'
                        : column.hideBelowPx >= 900
                          ? 'hidden lg:table-cell'
                          : column.hideBelowPx >= 768
                            ? 'hidden md:table-cell'
                            : column.hideBelowPx >= 640
                              ? 'hidden sm:table-cell'
                              : '';
                    const align = column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left';

                    if (column.key === 'movement') {
                      return (
                        <td key={column.key} className={`${rowPad} px-1 text-center ${hideClass}`}>
                          <Movement row={row} display={display} />
                        </td>
                      );
                    }

                    if (column.key === 'player') {
                      return (
                        <td key={column.key} className={`${rowPad} px-2 ${hideClass}`}>
                          <div className="flex items-center gap-2">
                            <span aria-hidden className={display ? 'text-2xl' : 'text-base'}>
                              {flagFor(row.country?.code)}
                            </span>
                            <span className={`${textSize} font-semibold leading-tight`}>{row.displayName}</span>
                            {badge ? (
                              <span className={`text-[10px] font-bold tracking-wider ${badge.tone}`}>{badge.label}</span>
                            ) : null}
                          </div>
                          {row.teamName && !display ? (
                            <span className="text-[11px] text-[var(--color-muted-dim)]">{row.teamName}</span>
                          ) : null}
                        </td>
                      );
                    }

                    if (column.key === 'primary') {
                      const value = cellValue(row, column);
                      const under = value.startsWith('-') || (payload.scope === 'net' && Number(value) > 36);
                      return (
                        <td key={column.key} className={`${rowPad} px-2 ${align} ${hideClass}`}>
                          <span
                            className={`tabular font-bold ${display ? 'text-[30px]' : 'text-[16px]'} ${
                              under ? 'text-[var(--color-fairway-300)]' : 'text-[var(--color-chalk)]'
                            }`}
                          >
                            {value}
                          </span>
                        </td>
                      );
                    }

                    return (
                      <td key={column.key} className={`${rowPad} px-2 ${align} ${hideClass}`}>
                        <span className={`${column.numeric ? 'tabular' : ''} ${display ? 'text-[22px]' : 'text-[13px]'} text-[var(--color-chalk)]`}>
                          {cellValue(row, column)}
                        </span>
                      </td>
                    );
                  })}
                </tr>
                {adEveryNRows > 0 && (i + 1) % adEveryNRows === 0 && i + 1 < rows.length ? (
                  <tr key={`ad-${row.entryId}`}>
                    <td colSpan={columns.length} className="px-2 py-3">
                      <AdZone zoneKey="inline_leaderboard" />
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      {!rows.length ? (
        <p className="px-4 py-10 text-center text-sm text-[var(--color-muted)]">
          No scores in yet. The board fills as cards come in from the course.
        </p>
      ) : null}
    </div>
  );
}
